// Artifact data layer (arc 101 slice 08) — the UI half of a capability that had
// REST (`/v1/artifacts`) and MCP (`artifact`) doors but no human one.
//
// An artifact is what an AGENT produces: a screenshot, a diagram, a dataset, a
// build output, stored in the account's own S3 bucket or the platform default.
// `read:artifacts` reads (every role), `write:artifacts` writes (member and up).
//
// THE OBJECT KEY NEVER REACHES THIS BUNDLE. `ApiArtifact` mirrors the API's
// `ArtifactView` (`packages/mcp/src/army/artifact.ts`), which structurally has no
// `key` field: a presigned URL is the only path to the bytes. Nothing here can
// leak one because nothing here is ever handed one.
//
// A PRESIGNED URL IS A CREDENTIAL — a bearer token in a query string, which is
// why all three presign routes answer `Cache-Control: no-store`. So the two calls
// that mint one are plain functions, NOT queries: nothing puts a live URL in the
// TanStack cache, in a query key, or in the router history. It is fetched on the
// click that needs it and lives in one signal until dismissed.
//
// NO CURSOR — but unlike `/v1/templates` this list is genuinely TRUNCATABLE.
// `GET /v1/artifacts` is a bounded head (default 50, ceiling 200) with no
// `next_cursor`, and the route says why: the port mints no cursor, so a permanent
// null would claim "that was all" about a response that may be cut short. This
// client therefore asks for the CEILING and the screen says so when it comes back
// full — the route's own advice is to narrow by `run_id`/`task_id`/`repo_id`, and
// that is the affordance the filter bar offers. A Load-more button would promise
// pages the server mints no token for.

import type { ArtifactVisibility } from '@developerz/domain';
import { createQuery } from '@tanstack/solid-query';
import { activeAccountId } from './active-account';
import { apiFetch } from './api';
import { SESSION_TOKEN } from './session';

export type { ArtifactVisibility };

/**
 * One artifact as `/v1/artifacts` serves it — the API's `ArtifactView`, snake_case,
 * ISO timestamps. Restated (not imported) because `@developerz/mcp` is not a
 * dashboard dependency; the field names are the wire contract both ends publish.
 * `expires_iso` is null for an artifact that never expires.
 */
export type ApiArtifact = {
  readonly id: string;
  readonly repo_id: string | null;
  readonly run_id: string | null;
  readonly task_id: string | null;
  readonly content_type: string;
  readonly size: number | null;
  readonly sha256: string | null;
  readonly created_by: string;
  readonly visibility: ArtifactVisibility;
  readonly expires_iso: string | null;
  readonly created_iso: string;
  readonly updated_iso: string;
};

/** An artifact plus a live presigned GET — what `…/url` and `…/share` answer. */
export type ApiArtifactAccess = ApiArtifact & {
  readonly url: string;
  readonly expires_in_seconds: number;
};

export const ARTIFACTS_QUERY_KEY = 'artifacts';

/**
 * The route's own `per_page` ceiling (`apps/api/src/routes/artifacts.ts`). Asked
 * for on every read so one round trip shows as much as the server will serve, and
 * compared against the row count so a FULL page can be labelled truncatable
 * rather than passed off as the whole collection.
 */
export const ARTIFACT_PAGE_MAX = 200;

/** `?run_id=&task_id=&repo_id=` — the narrowing the route advertises, all optional. */
export type ArtifactFilter = {
  readonly run_id?: string | undefined;
  readonly task_id?: string | undefined;
  readonly repo_id?: string | undefined;
};

/** The list query string, always carrying the ceiling. */
function listQuery(filter: ArtifactFilter): string {
  const params = new URLSearchParams();
  if (filter.run_id) params.set('run_id', filter.run_id);
  if (filter.task_id) params.set('task_id', filter.task_id);
  if (filter.repo_id) params.set('repo_id', filter.repo_id);
  params.set('per_page', String(ARTIFACT_PAGE_MAX));
  return `?${params.toString()}`;
}

/**
 * The account's artifacts, newest-first. The filter is part of the query KEY, so
 * narrowing re-reads server-side — filtering rows the client happens to hold
 * would hide everything past the head this read returned.
 */
export function useArtifacts(filter: () => ArtifactFilter, token: string | null = SESSION_TOKEN) {
  return createQuery<readonly ApiArtifact[]>(() => ({
    queryKey: [
      ARTIFACTS_QUERY_KEY,
      filter().run_id ?? '',
      filter().task_id ?? '',
      filter().repo_id ?? '',
      token,
      activeAccountId(),
    ],
    enabled: token !== null,
    queryFn: async () =>
      (await apiFetch<{ artifacts: ApiArtifact[] }>(`/v1/artifacts${listQuery(filter())}`, token))
        .artifacts,
  }));
}

/**
 * A short-lived presigned GET for the bytes. Mutates nothing — the 404 folds
 * unknown / foreign / bytes-never-landed / already-reaped into one honest answer.
 */
export async function artifactDownloadUrl(
  id: string,
  token: string | null = SESSION_TOKEN,
): Promise<ApiArtifactAccess> {
  return apiFetch<ApiArtifactAccess>(`/v1/artifacts/${encodeURIComponent(id)}/url`, token);
}

/**
 * Flip an artifact to `public-link` and presign it. DURABLE, audited, and it
 * notifies the account's members — no surface un-shares one, so the only way back
 * is to delete the artifact. That is why the screen confirms first.
 */
export async function shareArtifact(
  id: string,
  token: string | null = SESSION_TOKEN,
): Promise<ApiArtifactAccess> {
  return apiFetch<ApiArtifactAccess>(`/v1/artifacts/${encodeURIComponent(id)}/share`, token, {
    method: 'POST',
  });
}

/** Remove the object and its row. Idempotent server-side (204 either way). */
export async function deleteArtifact(
  id: string,
  token: string | null = SESSION_TOKEN,
): Promise<void> {
  await apiFetch<void>(`/v1/artifacts/${encodeURIComponent(id)}`, token, { method: 'DELETE' });
}
